import { InstallApp } from '../../shared/ui/InstallApp';
import { Link, useNavigate } from 'react-router';
import { JoinChurchForm } from '../churches/JoinChurchForm';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { Membership, MembershipStatus, MyAccount, MyContributions, PersonAttendance } from '../../shared/api/types';
import { formatDayHeading, formatTime } from '../calendar/dates';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync, type AsyncState } from '../../shared/ui/useAsync';
import { EmailVerificationNotice } from '../account/EmailVerificationNotice';
import { formatDay, formatMoney } from '../finance/money';
import { useMe, useSession } from '../session/SessionProvider';
import { Icon } from '../shell/nav';
import { relationLabel } from '../shell/ContextSwitcher';
import { ageFrom, baptizedLabel, formatBirthDate, genderLabel, PersonDetailsFields, readPersonDetails } from '../../shared/ui/PersonDetailsFields';
import { MyPublicProfileCard } from '../people/MyPublicProfileCard';
import { Profile360Card } from './Profile360Card';
import { MyGradesCard } from '../classrooms/Grades';
import { MyFamilyCard } from './MyFamily';
import { PhotoPicker } from '../../shared/ui/PhotoPicker';
import { PendingRequests, RejectedRequests } from '../churches/PendingRequests';
import { Avatar } from '../../shared/ui/Avatar';
import { showFieldErrors } from '../../shared/ui/fieldErrors';
import { useChurchCover } from './useChurchCover';

const STATUS_LABELS: Record<MembershipStatus, string> = {
  pending: 'Pendiente de aprobación',
  active: 'Miembro vigente',
  rejected: 'Solicitud rechazada',
  ended: 'Membresía finalizada',
};

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('es') : '');

/** Muestra una sección del perfil con sus propios estados (cada sección se autoriza por separado). */
function Section<T>({ title, state, children }: { title: string; state: AsyncState<T> & { reload: () => void }; children: (data: T) => React.ReactNode }) {
  return (
    <Card title={title}>
      {state.status === 'loading' ? <Loading /> : null}
      {state.status === 'error' ? <ErrorState error={state.error} onRetry={state.reload} /> : null}
      {state.status === 'ready' ? children(state.data) : null}
    </Card>
  );
}

/** Mis datos personales, con edición en el mismo lugar. */
function MyDetails({ account: p, onSaved }: { account: MyAccount; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const incomplete = !p.gender || !p.birthDate || !p.birthCountry || !p.address || p.baptized === null;

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const { details, error: missing, fields } = readPersonDetails(new FormData(formEl));
    if (missing) {
      setError(missing);
      showFieldErrors(formEl, fields);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.put('/identity/me', details);
      setEditing(false);
      onSaved();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <form className="form" onSubmit={save} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        <PersonDetailsFields person={p} idPrefix="me" />
        <div className="actions">
          <Button type="submit" busy={busy}>
            Guardar
          </Button>
          <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
            Cancelar
          </Button>
        </div>
      </form>
    );
  }

  const age = ageFrom(p.birthDate);
  return (
    <>
      {p.emailVerified ? null : <EmailVerificationNotice email={p.email} />}
      {incomplete ? <Alert tone="info">Completa tus datos: tu iglesia los necesita para tenerte bien registrado.</Alert> : null}
      <dl className="details">
        <dt>Nombre</dt>
        <dd>{p.givenName}</dd>
        <dt>Apellidos</dt>
        <dd>{[p.paternalSurname, p.maternalSurname].filter(Boolean).join(' ') || <span className="muted">Sin registrar</span>}</dd>
        <dt>Género</dt>
        <dd>{p.gender ? genderLabel(p.gender) : <span className="muted">Sin registrar</span>}</dd>
        <dt>Celular</dt>
        <dd>{p.mobilePhone ? <a href={`tel:${p.mobilePhone.replace(/[^\d+]/g, '')}`}>{p.mobilePhone}</a> : <span className="muted">Sin registrar</span>}</dd>
        <dt>Teléfono de casa</dt>
        <dd>{p.homePhone ? <a href={`tel:${p.homePhone.replace(/[^\d+]/g, '')}`}>{p.homePhone}</a> : <span className="muted">Sin registrar</span>}</dd>
        <dt>Nacimiento</dt>
        <dd>{p.birthDate ? `${formatBirthDate(p.birthDate)}${age !== null ? ` (${age} años)` : ''}` : <span className="muted">Sin registrar</span>}</dd>
        <dt>Lugar de nacimiento</dt>
        <dd>{p.birthPlace ?? <span className="muted">Sin registrar</span>}</dd>
        <dt>Dirección</dt>
        <dd>{p.address ?? <span className="muted">Sin registrar</span>}</dd>
        <dt>Bautizado</dt>
        <dd>{p.baptized === null ? <span className="muted">Sin registrar</span> : baptizedLabel(p.baptized)}</dd>
        <dt>Correo</dt>
        <dd>
          {p.email} {p.emailVerified ? <span className="badge badge--active">Confirmado</span> : <span className="badge badge--warn">Sin confirmar</span>}
        </dd>
      </dl>
      <div className="actions">
        <Button variant={incomplete ? 'primary' : 'secondary'} onClick={() => setEditing(true)}>
          {incomplete ? 'Completar mis datos' : 'Editar mis datos'}
        </Button>
      </div>
    </>
  );
}

/**
 * Perfil base. Perfil 360° es un agregador: compone datos de cada motor, no los posee.
 * Muestra Información, Familia, Membresía, relaciones y Asistencia; las demás áreas
 * (Servicio, Formación, …) se suman con sus módulos.
 */
export function ProfilePage() {
  const [sent, setSent] = useState(0);
  const me = useMe();
  const person = useAsync((signal) => api.get<MyAccount>('/identity/me', signal), [me.personId]);
  const memberships = useAsync(
    (signal) => api.get<{ items: (Membership & { churchName: string })[] }>('/multichurch/me/memberships', signal).then((r) => r.items),
    [me.personId],
  );

  const churchId = me.activeChurchId;
  const attendance = useAsync(
    (signal) =>
      churchId && me.canOwn('attendance.read')
        ? api.get<{ items: PersonAttendance[] }>(`/attendance/churches/${churchId}/people/${me.personId}`, signal).then((r) => r.items)
        : Promise.resolve(null),
    [churchId, me.personId],
  );

  // La portada es el logo de mi iglesia.
  const church = useChurchCover(churchId);

  const contributions = useAsync(
    (signal) =>
      churchId && me.canOwn('finance.read')
        ? api.get<MyContributions>(`/finance/churches/${churchId}/me/contributions`, signal)
        : Promise.resolve(null),
    [churchId, me.personId],
  );

  const { logout } = useSession();
  const navigate = useNavigate();
  async function onLogout() {
    await logout();
    navigate('/', { replace: true });
  }

  const fullName = person.status === 'ready' ? `${person.data.givenName} ${person.data.familyName}`.trim() : '';
  const cover = church.status === 'ready' && church.data ? church.data.logoUrl : null;

  return (
    <div className="page profile">
      <header className="wall-head">
        <div className="wall-cover" style={cover ? { backgroundImage: `url(${cover})` } : undefined} aria-hidden="true" />
        <div className="wall-head__main">
          <span className="profile-head__photo">
            <Avatar personId={me.personId} name={fullName} size={112} />
          </span>
          <div className="wall-head__name">
            <h1>{fullName || 'Mi perfil'}</h1>
            <p className="wall-head__tagline">Mi perfil</p>
            {me.activeContext ? (
              <p className="small muted">
                {me.activeContext.churchName} · {relationLabel(me.activeContext.relations)}
              </p>
            ) : null}
          </div>
          <div className="wall-head__actions">
            <PhotoPicker personId={me.personId} name={fullName} self buttonsOnly />
            {church.status === 'ready' && church.data ? (
              <Link className="btn btn--secondary" to={`/i/${church.data.slug}`}>
                Página de mi iglesia
              </Link>
            ) : null}
          </div>
        </div>
      </header>

      <div className="wall-body">
        <aside className="wall-side" aria-label="Mis datos">
          <Section title="Información" state={person}>
            {(p) => <MyDetails account={p} onSaved={person.reload} />}
          </Section>

        <Card title="Dónde participo">
          {me.contexts.length === 0 ? (
            <EmptyState title="Aún no participas en ninguna iglesia." />
          ) : (
            <ul className="list">
              {me.contexts.map((c) => (
                <li key={c.churchId} className="list__item">
                  <strong>{c.churchName}</strong>
                  <span className="muted">{relationLabel(c.relations)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Section title="Membresía" state={memberships}>
          {(items) =>
            items.length === 0 ? (
              <EmptyState title="Sin membresías todavía." />
            ) : (
              <ul className="list">
                {items.map((m) => (
                  <li key={m.id} className="list__item">
                    <strong>{m.churchName}</strong>
                    <span className={`badge badge--${m.status}`}>{STATUS_LABELS[m.status]}</span>
                    <span className="muted small">
                      {m.status === 'active' && `Desde ${formatDate(m.started_at)}`}
                      {m.status === 'ended' && `${formatDate(m.started_at)} – ${formatDate(m.ended_at)}`}
                      {(m.status === 'pending' || m.status === 'rejected') && `Solicitada el ${formatDate(m.requested_at)}`}
                    </span>
                  </li>
                ))}
              </ul>
            )
          }
        </Section>

        <MyFamilyCard personId={me.personId} />
        </aside>

        <div className="wall-feed">
          {churchId ? <Profile360Card churchId={churchId} personId={me.personId} /> : null}

          <MyPublicProfileCard personId={me.personId} />

        {attendance.status !== 'ready' || attendance.data ? (
          <Section title={`Mi asistencia${me.activeContext ? ` · ${me.activeContext.churchName}` : ''}`} state={attendance}>
            {(items) =>
              !items || items.length === 0 ? (
                <EmptyState title="Aún no hay asistencia registrada." />
              ) : (
                <ul className="list">
                  {items.slice(0, 10).map((a) => (
                    <li key={a.occasion.id} className="list__item">
                      <strong>{a.occasion.title}</strong>
                      <span className="muted small">
                        {formatDayHeading(new Date(a.occasion.startsAt))} · {formatTime(a.occasion.startsAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              )
            }
          </Section>
        ) : null}

        {contributions.status !== 'ready' || contributions.data ? (
          <Section title="Mis aportaciones" state={contributions}>
            {(data) =>
              !data || data.items.length === 0 ? (
                <EmptyState title="Aún no hay aportaciones registradas a tu nombre ni al de tu familia." />
              ) : (
                <>
                  {data.currency ? (
                    <p>
                      Total: <strong className="money">{formatMoney(data.totalCents, data.currency)}</strong>
                    </p>
                  ) : null}
                  <ul className="list">
                    {data.items.slice(0, 10).map((c) => (
                      <li key={c.id} className="list__item list__item--actions">
                        <span>
                          {c.category}
                          {c.family ? <span className="muted small"> · Familia {c.family}</span> : null} <span className="muted small">{formatDay(c.occurredOn)}</span>
                        </span>
                        <span className="money">{formatMoney(c.amountCents, c.currency)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )
            }
          </Section>
        ) : null}

        {churchId && me.can('departments.read') ? <MyGradesCard churchId={churchId} /> : null}

        <Card title="Otra iglesia">
          <p className="muted small">Busca la iglesia y pide unirte, usa su código o registra la tuya.</p>
          <p>
            <Link className="btn btn--secondary" to="/iglesias">Encuentra mi iglesia</Link>
          </p>
          <PendingRequests version={sent} />
          <RejectedRequests />
          <JoinChurchForm onSent={() => { setSent((v) => v + 1); memberships.reload(); }} />
          <p>
            <Link to="/app/iglesias/nueva">Registrar mi iglesia</Link>
          </p>
        </Card>

        {churchId && me.canOwn('approvals.read') ? (
          <Card title="Mis solicitudes">
            <p className="muted small">Lo que pediste (inscripciones, préstamos, gastos…) y si ya te lo aprobaron.</p>
            <p>
              <Link className="btn btn--secondary" to="/app/aprobaciones?list=mine">
                Ver mis solicitudes
              </Link>
            </p>
          </Card>
        ) : null}

        <Card title="Ajustes">
          <div className="home-settings">
            <Link to="/app/notificaciones" className="home-settings__row">
              <Icon name="bell" />
              <span>Notificaciones</span>
            </Link>
            <div className="home-settings__row">
              <Icon name="share" />
              <span>
                Instalar la app
                <span className="small muted"> · queda un ícono en tu pantalla y abre más rápido.</span>
                <InstallApp />
              </span>
            </div>
            <button type="button" className="home-settings__row" onClick={() => void onLogout()}>
              <Icon name="logout" />
              <span>Cerrar sesión</span>
            </button>
          </div>
        </Card>

        </div>
      </div>
    </div>
  );
}
